import { lazy, Suspense, useEffect, useState } from 'react'
import { Toaster } from './ds/index.js'
import { XBtn } from './ui/kit'
import { match, usePath } from './lib/router'
import { api } from './lib/api'
import { embedded, openFull, tellPortal } from './lib/embed'

const Events = lazy(() => import('./pages/Events'))
const Prep = lazy(() => import('./pages/Prep'))
const Studio = lazy(() => import('./pages/Studio'))
const Report = lazy(() => import('./pages/Report'))
const Analytics = lazy(() => import('./pages/Analytics'))
const People = lazy(() => import('./pages/People'))
const Room = lazy(() => import('./pages/Room'))
const Recorder = lazy(() => import('./pages/Recorder'))
const Join = lazy(() => import('./pages/Join'))
const SignIn = lazy(() => import('./pages/SignIn'))

export interface MeInfo {
  actor: null | { kind: 'staff'; email: string; name: string; owner: boolean; access: import('@shared/perms').AppAccess; picture?: string | null } | { kind: 'invite'; name: string; role: string; eventId: string }
  signin: { google: boolean; dev: boolean; portal: string | null }
  providers: { id: string; label: string; configured: boolean; production: boolean }[]
}

export function useMe(): { me: MeInfo | null; reload: () => void } {
  const [me, setMe] = useState<MeInfo | null>(null)
  const [n, setN] = useState(0)
  useEffect(() => { api<MeInfo>('/api/me').then(setMe).catch(() => setMe({ actor: null, signin: { google: false, dev: false, portal: null }, providers: [] })) }, [n])
  return { me, reload: () => setN(x => x + 1) }
}

function Loading() {
  return <div style={{ padding: 40, color: 'var(--mut)' }} role="status">Loading…</div>
}

export function App() {
  const full = usePath()
  const path = full.split('?')[0]
  useEffect(() => { tellPortal(full) }, [full])
  let page: JSX.Element
  let m: Record<string, string> | null
  if (path === '/signin') page = <SignIn />
  else if (path === '/') page = <Events />
  else if (path === '/analytics') page = <Analytics />
  else if (path === '/people') page = <People />
  else if ((m = match('/events/:id/studio', path))) page = <Studio eventId={m.id} />
  else if ((m = match('/events/:id/report', path))) page = <Report eventId={m.id} />
  else if ((m = match('/events/:id', path))) page = <Prep eventId={m.id} />
  else if ((m = match('/join/:token', path))) page = <Join token={m.token} />
  else if ((m = match('/r/:id', path))) page = <Recorder eventId={m.id} />
  else if ((m = match('/:slug', path))) page = <Room slug={m.slug} />
  else page = <div style={{ padding: 40 }}>Not found</div>
  // The studio and the room need the whole window (the studio's controls live
  // in its top bar, which the portal's frame hides).
  const staffPage = path === '/' || path === '/signin' || path === '/analytics' || path === '/people' || !!match('/events/:id', path) || !!match('/events/:id/report', path)
  if (embedded && !staffPage) page = <OpenFull path={full} />
  return (
    <>
      <Suspense fallback={<Loading />}>{page}</Suspense>
      {!path.startsWith('/r/') && <Toaster />}
    </>
  )
}

function OpenFull({ path }: { path: string }) {
  const studio = !!match('/events/:id/studio', path.split('?')[0])
  return (
    <div style={{ padding: 'var(--sp-9) var(--sp-7)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--sp-4)' }}>
      <div style={{ fontWeight: 800, fontSize: 'var(--type-card-title)' }}>{studio ? 'The studio opens in its own tab' : 'This page opens in its own tab'}</div>
      <div style={{ color: 'var(--tx2)' }}>It needs the whole window.</div>
      <XBtn solid icon="external-link" onClick={() => openFull(path)}>{studio ? 'Open the studio' : 'Open it'}</XBtn>
      <XBtn ghost size="sm" icon="arrow-left" onClick={() => history.back()}>Back</XBtn>
    </div>
  )
}
